// src/lib/authorization.js

import * as React from 'react';
import { useAuth } from './auth';

// --- Define roles and dummy permissions (as before) ---
export const ROLES = {
  ROOT_USER: 'superAdmin',
  KYC_AGENT: 'kycAgent',
  COMPLIANCE_OFFICER: 'complianceOfficer',
  TRANSACTION_MANAGER: 'transactionManager',
  CUSTOMER_SUPPORT: 'customerSupport',
  AUDIT: 'audit',
  TECHNICAL: 'technical',
};

const PERMISSIONS = {
  superAdmin: [
    'manageUsers',
    'manageRoles',
    'systemSettings',
    'accessAuditLogs',
    'overrideComplianceFlags',
    'fullAccess',
  ],
  complianceOfficer: [
    'viewTransactions',
    'flagSuspiciousActivity',
    'approveKYCAML',
    'accessCustomerProfiles',
    'generateComplianceReports',
    'manageBlacklists',
  ],
  kycAgent: [
    'viewKYCDocuments',
    'approveRejectKYC',
    'updateCustomerProfiles',
    'viewTransactionHistory',
  ],
  transactionManager: [
    'viewTransactions',
    'retryFailedTransactions',
    'suspendTransactions',
    'approveLargeTransactions',
    'managePayoutRules',
  ],
  audit: [
    'viewAllTransactions',
    'viewLogs',
    'downloadAuditReports',
    'viewUserActivities',
  ],
  technical: [
    'manageAPIKeys',
    'monitorSystemHealth',
    'accessDevOpsLogs',
    'updateWebhooks',
    'manageConfiguration',
  ],
  customerSupport: [
    'viewCustomerProfiles',
    'viewTransactions',
    'initiateRefunds',
    'resetUserCredentials',
    'createSupportTickets',
  ],
};

export const useAuthorization = () => {
  const { user } = useAuth();
  if (!user) throw new Error('User does not exist!');

  const role = user.accountType;

  const permissions = PERMISSIONS[role] || [];

  const checkRoleAccess = React.useCallback(
    (allowedRoles = []) => {
      return allowedRoles.length === 0 || allowedRoles.includes(role);
    },
    [role]
  );

  const checkPermissions = React.useCallback(
    (requiredPermissions = []) => {
      return (
        requiredPermissions.length === 0 ||
        requiredPermissions.every((p) => permissions.includes(p))
      );
    },
    [permissions]
  );

  return {
    role,
    permissions,
    checkRoleAccess,
    checkPermissions,
  };
};

export const Authorization = ({
  allowedRoles = [],
  requiredPermissions = [],
  policyCheck,
  forbiddenFallback = null,
  children,
}) => {
  const { checkRoleAccess, checkPermissions } = useAuthorization();

  const hasRole = checkRoleAccess(allowedRoles);
  const hasPermission = checkPermissions(requiredPermissions);

  let canAccess = hasRole && hasPermission;

  if (typeof policyCheck === 'boolean') {
    canAccess = canAccess && policyCheck;
  }

  return <>{canAccess ? children : forbiddenFallback}</>;
};
